import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, MessageCircle, AlertCircle, ShoppingBag, ChevronRight } from 'lucide-react';
import { PlaceOrderResponse } from './typres';

const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);

const SUPPORT_USERNAME = process.env.REACT_APP_SUPPORT_TG_USERNAME || 'your_support_username';

const OrderSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderResponse = location.state?.orderResponse as PlaceOrderResponse | undefined;

  if (!orderResponse) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
        <div className="text-center">
          <p className="text-foreground/50">Информация о заказе недоступна.</p>
          <button onClick={() => navigate('/')} className="mt-4 text-sm text-red-600 dark:text-red-400 underline">
            На главную
          </button>
        </div>
      </div>
    );
  }

  const { order, notificationSent, hasTelegram } = orderResponse;

  return (
    <div className="min-h-screen overflow-x-hidden bg-background px-4 py-8 text-foreground">
      <div className="mx-auto max-w-lg">

        {/* Success icon */}
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30">
            <CheckCircle2 className="h-10 w-10 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold sm:text-3xl">Заказ оформлен!</h1>
          <p className="mt-1 text-foreground/40">#{order.id}</p>
        </div>

        {/* Telegram notification status */}
        {notificationSent ? (
          <div className="mb-4 flex items-start gap-3 rounded-[16px] border border-emerald-500/20 bg-emerald-500/8 p-4">
            <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
            <p className="text-sm text-emerald-300">
              Мы отправили детали заказа вам в Telegram.
            </p>
          </div>
        ) : !hasTelegram ? (
          <div className="mb-4 flex items-start gap-3 rounded-[16px] border border-amber-500/20 bg-amber-500/8 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-300">Telegram не привязан</p>
              <p className="mt-0.5 text-xs text-amber-300/70">
                Откройте приложение через Telegram, чтобы получать уведомления о заказах прямо в чат.
              </p>
            </div>
          </div>
        ) : (
          <div className="mb-4 flex items-start gap-3 rounded-[16px] border border-foreground/10 bg-foreground/4 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-foreground/40" />
            <p className="text-sm text-foreground/50">
              Не удалось отправить уведомление. Проверьте, что бот не заблокирован.
            </p>
          </div>
        )}

        {/* Order items */}
        <div className="mb-4 rounded-[20px] border border-foreground/10 bg-foreground/4 overflow-hidden">
          <div className="border-b border-foreground/8 px-4 py-3">
            <p className="text-sm font-medium text-foreground/60">Состав заказа</p>
          </div>
          <div className="divide-y divide-foreground/6">
            {order.orderProducts.map((item, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  {item.product.picture && (
                    <img
                      src={item.product.picture}
                      alt={item.product.name}
                      className="h-10 w-10 rounded-lg object-cover shrink-0"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{item.product.name}</p>
                    <p className="text-xs text-foreground/40">× {item.quantity}</p>
                  </div>
                </div>
                <span className="shrink-0 text-sm font-semibold text-foreground/80 ml-3">
                  {fmt(item.unitPrice * item.quantity)} ₽
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-foreground/10 px-4 py-3">
            <span className="text-sm text-foreground/50">Итого</span>
            <span className="text-lg font-bold text-red-600 dark:text-red-400">{fmt(order.totalPrice)} ₽</span>
          </div>
        </div>

        {/* Payment instruction */}
        <div className="mb-6 rounded-[20px] border border-foreground/10 bg-foreground/4 px-4 py-4">
          <p className="mb-1 text-sm font-medium">Как оплатить</p>
          <p className="text-sm text-foreground/50">
            Напишите нашему менеджеру в Telegram — он пришлёт реквизиты и подтвердит заказ.
          </p>
          <a
            href={`https://t.me/${SUPPORT_USERNAME}`}
            target="_blank"
            rel="noreferrer"
            className="mt-3 flex items-center justify-between rounded-[12px] bg-[#229ED9]/15 px-4 py-3 transition-colors hover:bg-[#229ED9]/25"
          >
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-[#229ED9]" />
              <span className="text-sm font-medium text-[#229ED9]">@{SUPPORT_USERNAME}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-[#229ED9]/60" />
          </a>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <Link
            to="/orders"
            className="flex items-center justify-center gap-2 rounded-[14px] border border-foreground/10 bg-foreground/5 py-3 text-sm font-medium text-foreground/70 transition-colors hover:bg-foreground/10"
          >
            <ShoppingBag className="h-4 w-4" />
            Мои заказы
          </Link>
          <Link
            to="/"
            className="flex items-center justify-center rounded-[14px] bg-red-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-500"
          >
            Продолжить покупки
          </Link>
        </div>

      </div>
    </div>
  );
};

export default OrderSuccessPage;
